import React, { memo, useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import LinearProgress from '@material-ui/core/LinearProgress';
import BrightnessIcon from '@material-ui/icons/Brightness6';
import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import LabelIcon from '@material-ui/icons/Label';
import LabelOffIcon from '@material-ui/icons/LabelOff';
import LayersIcon from '@material-ui/icons/Layers';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';
import { FormPanel, PromptContext } from '@eas/common-web';
import { MapContainer, WorkOrderMarker } from '@composite/map';
import {
  MapTechnicalObjectsContext,
  useTechnicalObjectsOnMap,
} from '@composite/map/context/map-technical-objects-context';
import MapControlButton from '@composite/map/controls/map-control-button';
import { useMapControl } from '@composite/map/controls/map-control-hook';
import { useMapTileLayerControl } from '@composite/map/controls/map-control-tile-layers';
import { MapControlWrapper } from '@composite/map/controls/map-control-wrapper';
import TechnicalObjectMapMarker from '@composite/map/markers/technical-objects/technical-object-marker';
import { GPSPoint, TechnicalObject } from '@models';

type Props = {
  selected?: string;
  setSelected: (fid?: string) => void;
  woId: string;
  woCoords: GPSPoint;
  technicalObjects: TechnicalObject[];
  woIdentifier?: number;
  loading?: boolean;
};

export const MuzaMapViewMemo = memo(
  MuzaMapView,
  (prev, next) =>
    prev.selected === next.selected &&
    prev.woId === next.woId &&
    prev.loading === next.loading
);

function MuzaMapView(props: Props) {
  const {
    selected,
    setSelected,
    woId,
    woCoords,
    technicalObjects,
    woIdentifier,
    loading,
  } = props;

  const { testPrompt } = useContext(PromptContext);
  const { mapRef, zoomIn, zoomOut, resetView } = useMapControl();
  const { TileLayer, nextTileLayer, tileLayerRef, switchContrast } =
    useMapTileLayerControl();

  const { setShowLabels, showLabels } = useTechnicalObjectsOnMap({
    initialShowLabels: true,
  });

  const [centerCoords, setCenterCoords] = useState<GPSPoint>(woCoords);
  useEffect(() => {
    setCenterCoords(woCoords);
    resetView(woCoords, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woId]);

  useEffect(() => {
    if (selected) {
      const coords = technicalObjects.find((t) => t.fid === selected)
        ?.coordinates?.[0];
      if (coords) {
        setCenterCoords(coords as GPSPoint);
        resetView(coords, 20);
      }
    }
  }, [selected, technicalObjects, mapRef, resetView]);

  if (loading) {
    return <LinearProgress />;
  }
  return (
    <FormPanel
      defaultExpanded={true}
      expandable={true}
      label={
        <FormattedMessage
          id="ZSD__WORK_ORDERS__MUZA__PANEL__TECH_OBJECTS_MAP"
          defaultMessage="Zobrazenie na mape"
        />
      }
    >
      <MapTechnicalObjectsContext.Provider
        value={{ showLabels, setShowLabels }}
      >
        <div style={{ width: '100%', height: 600 }}>
          <MapContainer
            key={`map-${woId}`}
            zoomControl={false}
            ref={mapRef}
            doubleClickZoom={false}
            initialZoom={19}
            initialCenter={centerCoords}
          >
            <MapControlWrapper position="topright">
              <MapControlButton
                label={<LayersIcon />}
                onClick={nextTileLayer}
                tooltip="Prepnúť vrstvu"
              />
              <MapControlButton
                label={<BrightnessIcon />}
                onClick={switchContrast}
                tooltip="Zmeniť viditeľnosť vrstvy"
              />
              <MapControlButton
                label={<ZoomInIcon />}
                onClick={zoomIn}
                tooltip="Priblížť"
              />
              <MapControlButton
                label={<ZoomOutIcon />}
                onClick={zoomOut}
                tooltip="Oddialiť"
              />
              <MapControlButton
                label={<CenterFocusStrongIcon />}
                onClick={() => resetView(centerCoords, 20)}
                tooltip="Vycentrovať"
              />

              <MapTechnicalObjectsContext.Consumer>
                {({ showLabels, setShowLabels }) => (
                  <MapControlButton
                    label={showLabels ? <LabelIcon /> : <LabelOffIcon />}
                    onClick={() => setShowLabels?.((p) => !p)}
                    tooltip="Zobraziť popisky"
                  />
                )}
              </MapTechnicalObjectsContext.Consumer>
              {selected && (
                <>
                  <MapControlButton
                    label={<OpenInNewIcon />}
                    color="default"
                    onClick={() => {
                      const id = technicalObjects.find(
                        (to) => to.fid === selected
                      )?.id;
                      if (id) {
                        window.open(`/zsd/technicke-objekty/${id}`, '_blank');
                      }
                    }}
                    tooltip="Zobraziť detail objektu v novom okne"
                  />
                  <MapControlButton
                    label={<OpenInBrowserIcon />}
                    tooltip="Otvoriť detail objektu v popup okne"
                    color="default"
                    onClick={() => {
                      const id = technicalObjects.find(
                        (to) => to.fid === selected
                      )?.id;
                      testPrompt({
                        key: `TECHNICAL_OBJECT_DETAIL_DIALOG_${id}`,
                        submitCallback: () => {},
                      });
                    }}
                  />
                </>
              )}
            </MapControlWrapper>
            {woCoords && (
              <WorkOrderMarker
                tooltip={{
                  content: `Pracovný príkaz ${woIdentifier}`,
                  permanent: true,
                }}
                label=""
                point={[woCoords.latitude!, woCoords.longitude!]}
              />
            )}
            {(technicalObjects ?? []).map((to) => (
              <TechnicalObjectMapMarker
                key={to.fid}
                isSelected={selected === to.fid}
                techObject={to}
                onClick={() => {
                  setSelected(to.fid);
                }}
              />
            ))}

            <TileLayer ref={tileLayerRef} />
          </MapContainer>
        </div>
      </MapTechnicalObjectsContext.Provider>
    </FormPanel>
  );
}
